import {
    CashFlowSectionId,
    NON_EXPANDABLE_CASH_FLOW_SECTIONS,
} from "@common/constants/reports/cashflow.constant";
import {
    CashFlowColumn,
    CashFlowReportRowPath,
    CashFlowTableRow,
} from "@common/dto/reports/cash-flow-tab-report-response.dto";
import {
    CASH_FLOW_PERCENT_DIFF_SUFFIX,
    CASH_FLOW_VALUE_DIFF_SUFFIX,
} from "@common/dto/reports/reports-common.dto";
import classNames from "classnames";
import React, { MouseEventHandler, useCallback } from "react";
import { noop } from "@common/helpers/function";
import { Button } from "@design-system/Button/Button";
import { DollarIcon, PercentageIcon, TriangleIcon } from "@icons";
import { bem } from "../../../bem";
import { useCashFlowDrillDownContext } from "../CashFlowDrillDown.context";
import "./CashFlowReportCell.scss";

interface RenderCellProps {
    column: CashFlowColumn;
    row: CashFlowTableRow;
    isExpanded?: boolean;
    isTitle?: boolean;
    setIsExpanded?: (value: boolean) => void;
    toggleRow?: (id: string) => void;
    rowVisibility?: Record<string, boolean>;
    path: CashFlowReportRowPath[];
    stickyRightOffset?: number;
}

const block = bem("data-table");

export const CashFlowReportCell: React.FC<RenderCellProps> = ({
    row,
    column,
    isTitle = false,
    isExpanded = false,
    setIsExpanded,
    toggleRow = noop,
    rowVisibility = {},
    path,
    stickyRightOffset,
}) => {
    const expandToggle = useCallback<
        MouseEventHandler<HTMLTableCellElement>
    >(() => {
        setIsExpanded?.(!isExpanded);
    }, [isExpanded, setIsExpanded]);

    const rowCanExpand =
        row.children?.length &&
        !NON_EXPANDABLE_CASH_FLOW_SECTIONS.includes(
            row.id as CashFlowSectionId,
        );

    const percentDiffRowId = `${row.id}${CASH_FLOW_PERCENT_DIFF_SUFFIX}`;
    const valueDiffRowId = `${row.id}${CASH_FLOW_VALUE_DIFF_SUFFIX}`;

    const handlePercentClick = useCallback<
        MouseEventHandler<HTMLButtonElement>
    >(
        (event) => {
            event.stopPropagation();
            toggleRow(percentDiffRowId);
        },
        [percentDiffRowId, toggleRow],
    );

    const handleDiffClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
        (event) => {
            event.stopPropagation();
            toggleRow(valueDiffRowId);
        },
        [valueDiffRowId, toggleRow],
    );

    const { open: openTransactionsModal } = useCashFlowDrillDownContext();

    const isPercentDiffVisible = rowVisibility[percentDiffRowId];
    const isValueDiffVisible = rowVisibility[valueDiffRowId];

    if (isTitle) {
        const onCellClick: MouseEventHandler<HTMLTableCellElement> = (
            event,
        ) => {
            if (rowCanExpand) {
                expandToggle(event);
            }
        };

        return (
            <td
                className={block("cell", {
                    title: true,
                    expandable: rowCanExpand,
                    summary: row.isSummary,
                })}
                onClick={onCellClick}
            >
                <div>
                    <span
                        className={classNames({
                            "data-table__cell--expandable": rowCanExpand,
                            "data-table__cell--expanded": isExpanded,
                        })}
                    >
                        {row.data?.[column.id] ?? <>&nbsp;</>}
                        {rowCanExpand ? (
                            <TriangleIcon
                                className={classNames(
                                    "icon-color-gray-500 ml-2",
                                    {
                                        "icon-rotate-270": !isExpanded,
                                    },
                                )}
                            />
                        ) : null}
                    </span>

                    <span className="cash-flow-cell-addon">
                        <Button
                            variant="secondary"
                            size="sm"
                            iconOnly
                            onClick={handlePercentClick}
                            aria-label={
                                isPercentDiffVisible
                                    ? "Hide percent difference"
                                    : "Show percent difference"
                            }
                            hidden={!(percentDiffRowId in rowVisibility)}
                        >
                            <PercentageIcon />
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            iconOnly
                            onClick={handleDiffClick}
                            aria-label={
                                isValueDiffVisible
                                    ? "Hide dollar difference"
                                    : "Show dollar difference"
                            }
                            hidden={!(valueDiffRowId in rowVisibility)}
                        >
                            <DollarIcon />
                        </Button>
                    </span>
                </div>
            </td>
        );
    }

    const onCellClick: MouseEventHandler<HTMLTableCellElement> = () => {
        openTransactionsModal({
            category: row.filters?.category,
            counterpartyId: row.filters?.counterpartyId,
            direction: row.filters?.direction,
            withEmptyOrUnverifiedCounterparty:
                row.filters?.withEmptyOrUnverifiedCounterparty,
            hasCounterparty: row.filters?.hasCounterparty,
            path,
            columnId: column.id,
        });
    };

    const cellValue = row.data?.[column.id];
    const sentiment = row.options?.[column.id]?.sentiment;

    const hasValue = cellValue !== undefined;

    const isParentRow = path.length === 1;

    const isRowExpandableAndExpanded = rowCanExpand && isExpanded;
    const isRowExpandableAndNotExpanded = rowCanExpand && !isExpanded;

    const isPercentageColumnOfNonExpandedRow =
        column.id === "percent" &&
        rowCanExpand &&
        isParentRow &&
        isRowExpandableAndNotExpanded;

    const shouldShowEmptyValue =
        !!isPercentageColumnOfNonExpandedRow ||
        !hasValue ||
        !!isRowExpandableAndExpanded;

    const isComparisonRow = !!row.isComparison;

    const emptyValue =
        (!rowCanExpand && !hasValue) ||
        isPercentageColumnOfNonExpandedRow ||
        isComparisonRow ? (
            <>-</>
        ) : null;

    const isClickable =
        column.id !== "percent" && !shouldShowEmptyValue && !isComparisonRow;

    return (
        <td
            className={block("cell", {
                positive: sentiment === "positive",
                negative: isComparisonRow && sentiment !== "positive",
                clickable: isClickable,
                summary: row.isSummary,
            })}
            style={{
                ...(stickyRightOffset && { right: stickyRightOffset }),
                ...(column.id === "percent" && {
                    paddingRight: "0",
                    paddingLeft: "0.75rem",
                }),
            }}
            onClick={isClickable ? onCellClick : undefined}
        >
            {shouldShowEmptyValue ? emptyValue : cellValue}
        </td>
    );
};
